import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent as ReactDragEvent,
  type JSX,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import dayjs from 'dayjs';
import {
  Box,
  styled,
} from '@mui/material';
import type { IBiomaterial, IEvent } from '@zero-dash/types';
import CustomTypography from '@/components/Common/Typography';
import { corePalette } from '@/themes/colours';
import useWidth from '@/hooks/useWidth';
import {
  BIOMARKER_LOADING_STATE,
  DAY_MS,
  DEFAULT_TRACK_ORDER,
  DISEASE_BURDEN_LOADING_STATE,
  HOVER_CLOSE_DELAY_MS,
  IS_TREATMENTS_SECTION_VISIBLE,
  LABEL_WIDTH,
  MONITORING_VIEW_PADDING_DAYS,
  REPORT_TRACKS,
  TIMELINE_COLOURS,
} from '@/constants/PatientDashboard/patientTimeline';
import type {
  IAxisHoverState,
  IBiomarkerFetchState,
  IDiseaseBurdenFetchState,
  IHoverState,
  ITimelineData,
  ITrackDragState,
  ITrackOrderState,
  ReorderableSectionKey,
  SectionKey,
  ViewWindow,
} from '@/types/PatientDashboard/PatientTimeline.types';
import type { IAnalysisSet } from '@/types/Analysis/AnalysisSets.types';
import type { IBiosample } from '@/types/Analysis/Biosamples.types';
import type { IReport } from '@/types/Reports/Reports.types';
import AxisCrosshair from './AxisCrosshair';
import BiomarkerPanel from './BiomarkerPanel';
import DiseaseBurdenPanel from './DiseaseBurdenPanel';
import FloatingTooltip from './FloatingTooltip';
import Header from './Header';
import Marker from './Marker';
import MonthAxis from './MonthAxis';
import Overview from './Overview';
import Section from './Section';
import TimelinePopup from './TimelinePopup';
import LaneError from './LaneError';
import TrackRow from './TrackRow';
import {
  buildTimelineData,
  clampView,
  computeMarkerLabelWidths,
  dateToFraction,
  getMinViewWidth,
  getValidDate,
  orderTrackRows,
  reorderTrackIds,
} from './PatientTimeline.util';
import { buildTimelinePopupContent, buildReportPopupContentByDate } from './TimelinePopup.util';
import { buildBiomarkerData } from './Biomarker.util';
import { buildDiseaseBurdenData } from './DiseaseBurden.util';
import { buildChartLaneLogScale, isWithinChartLaneView } from './ChartLane.util';
import { useBiomarkerSelection } from '@/hooks/PatientDashboard/useBiomarkerSelection';
import { useDiseaseBurdenFilters } from '@/hooks/PatientDashboard/useDiseaseBurdenFilters';

const CARD_PADDING = '16px';

const VIEW_WIDTH_EPSILON = 1e-9;

const TimelineCard = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  padding: `0 ${CARD_PADDING} ${CARD_PADDING}`,
  gap: '12px',
  position: 'relative',
  width: '100%',
  background: corePalette.white,
  borderRadius: '12px',
  border: `1px solid ${corePalette.grey30}`,
  color: corePalette.offBlack100,
});

const HelperText = styled(CustomTypography)({
  width: '100%',
  fontSize: '12px',
  lineHeight: '130%',
  color: corePalette.grey100,
});

const STICKY_HEADER_Z_INDEX = 3;

const StickyTimelineHeader = styled(Box)({
  position: 'sticky',
  top: 0,
  zIndex: STICKY_HEADER_Z_INDEX,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  paddingTop: CARD_PADDING,
  background: corePalette.white,
});

const TimelinePanel = styled(Box)({
  position: 'relative',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
});

const InnerWrapper = styled(Box)({
  position: 'relative',
  width: '100%',
  borderRadius: '6px',
  overflow: 'hidden',
  '& > *:not(:first-of-type)': {
    borderTop: `1px solid ${TIMELINE_COLOURS.neutralBorder}`,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: '6px',
    border: `1px solid ${TIMELINE_COLOURS.neutralBorder}`,
    pointerEvents: 'none',
  },
});

interface IProps {
  patientId?: string;
  registrationDate?: string;
  firstCollection?: string;
  biomaterials?: IBiomaterial[];
  biosamples?: IBiosample[];
  events?: IEvent[];
  reports?: IReport[];
  isEventsLoading?: boolean;
  isSamplesLoading?: boolean;
  isReportsLoading?: boolean;
  hasSamplesFailure?: boolean;
  biomarker?: IBiomarkerFetchState;
  diseaseBurden?: IDiseaseBurdenFetchState;
  analysisSets?: IAnalysisSet[];
  onRetrySamples?: () => void;
  onRetryBiomarkers?: () => void;
  onRetryDiseaseBurden?: () => void;
}

export default function PatientTimeline({
  patientId,
  biomaterials,
  biosamples,
  registrationDate,
  firstCollection,
  events,
  reports,
  isEventsLoading = false,
  isSamplesLoading = false,
  isReportsLoading = false,
  hasSamplesFailure = false,
  biomarker = BIOMARKER_LOADING_STATE,
  diseaseBurden = DISEASE_BURDEN_LOADING_STATE,
  analysisSets,
  onRetrySamples,
  onRetryBiomarkers,
  onRetryDiseaseBurden,
}: IProps): JSX.Element {
  const [view, setView] = useState<ViewWindow>([0, 1]);
  const [openSections, setOpenSections] = useState<Record<SectionKey, boolean>>({
    events: true,
    samples: true,
    reports: true,
    treatments: true,
    diseaseBurden: true,
    biomarker: true,
  });
  const [trackOrder, setTrackOrder] = useState<ITrackOrderState>(() => ({
    reports: [...DEFAULT_TRACK_ORDER.reports],
    samples: [...DEFAULT_TRACK_ORDER.samples],
  }));
  const [draggedTrack, setDraggedTrack] = useState<ITrackDragState | null>(null);
  const [dropTargetTrack, setDropTargetTrack] = useState<ITrackDragState | null>(null);
  const [hover, setHover] = useState<IHoverState>();
  const [axisHover, setAxisHover] = useState<IAxisHoverState | null>(null);
  const lastHoverKey = useRef<string>('');
  const closeHoverTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [panelRef, panelWidth] = useWidth<HTMLDivElement>();
  const trackContentWidth = Math.max(0, panelWidth - LABEL_WIDTH);

  const timelineData = useMemo<ITimelineData>(
    () => buildTimelineData({
      events,
      biomaterials,
      biosamples,
      reports,
      registrationDate,
      firstCollection,
    }),
    [biomaterials, biosamples, events, reports, firstCollection, registrationDate],
  );

  const minViewWidth = useMemo(
    () => getMinViewWidth(timelineData.range),
    [timelineData.range],
  );

  const biomarkerData = useMemo(
    () => buildBiomarkerData(
      biomarker.snvs,
      (analysisSets ?? []).flatMap((set) => set.biosamples ?? []),
      biomaterials ?? [],
    ),
    [analysisSets, biomarker.snvs, biomaterials],
  );

  const diseaseBurdenData = useMemo(
    () => buildDiseaseBurdenData(diseaseBurden.results, analysisSets ?? [], biomaterials ?? []),
    [analysisSets, biomaterials, diseaseBurden.results],
  );

  const chartLaneScale = useMemo(
    () => buildChartLaneLogScale([
      ...diseaseBurdenData.analytes.flatMap(
        (analyte) => analyte.points.flatMap((point) => [point.purity, point.lod]),
      ),
      ...biomarkerData.lines.flatMap((line) => line.points.map((point) => point.vaf)),
    ]),
    [biomarkerData.lines, diseaseBurdenData.analytes],
  );

  const {
    filters, hiddenAnalytes, toggleAssay, setAssayMode, toggleVisibility,
  } = useDiseaseBurdenFilters(diseaseBurdenData);
  const { selection, toggleVariant } = useBiomarkerSelection(biomarkerData);

  const reportMarkerIds = useMemo(
    () => new Set(timelineData.reportMarkers.map((marker) => marker.id)),
    [timelineData.reportMarkers],
  );

  const reportPopupContent = useMemo(
    () => buildReportPopupContentByDate(
      timelineData.reportMarkers,
      trackOrder.reports,
      patientId,
    ),
    [patientId, timelineData.reportMarkers, trackOrder.reports],
  );

  const timelinePopupContent = useMemo(
    () => buildTimelinePopupContent({
      eventMarkers: timelineData.eventMarkers,
      biomaterials,
      reports,
      analysisSets,
      diseaseBurden: { data: diseaseBurdenData, filters, hiddenAnalytes },
      biomarker: { data: biomarkerData, selection },
      patientId,
    }),
    [
      analysisSets,
      biomarkerData,
      biomaterials,
      diseaseBurdenData,
      filters,
      hiddenAnalytes,
      patientId,
      reports,
      selection,
      timelineData.eventMarkers,
    ],
  );
  const orderedSampleTracks = useMemo(
    () => orderTrackRows(timelineData.sampleTracks, trackOrder.samples),
    [timelineData.sampleTracks, trackOrder.samples],
  );
  const orderedReportTracks = useMemo(
    () => {
      const trackIdsWithReports = new Set<string>(
        timelineData.reportMarkers.map((marker) => marker.reportTrackId),
      );
      return orderTrackRows(
        REPORT_TRACKS.filter((track) => trackIdsWithReports.has(track.id)),
        trackOrder.reports,
      );
    },
    [timelineData.reportMarkers, trackOrder.reports],
  );
  const xPct = useMemo(() => (date: string): number => {
    const [viewStart, viewEnd] = view;
    const fraction = dateToFraction(date, timelineData.range);
    return ((fraction - viewStart) / (viewEnd - viewStart)) * 100;
  }, [timelineData.range, view]);

  const isInView = useMemo(
    () => (date: string): boolean => isWithinChartLaneView(xPct(date)),
    [xPct],
  );

  const sampleLabelWidths = useMemo(() => {
    const widths = new Map<string, number | null>();
    orderedSampleTracks.forEach((track) => {
      computeMarkerLabelWidths(track.markers, xPct, trackContentWidth).forEach((value, id) => {
        widths.set(id, value);
      });
    });
    return widths;
  }, [trackContentWidth, orderedSampleTracks, xPct]);

  const reportLabelWidths = useMemo(() => {
    const widths = new Map<string, number | null>();
    orderedReportTracks.forEach((track) => {
      const trackMarkers = timelineData.reportMarkers.filter(
        (marker) => marker.reportTrackId === track.id,
      );
      computeMarkerLabelWidths(trackMarkers, xPct, trackContentWidth).forEach((value, id) => {
        widths.set(id, value);
      });
    });
    return widths;
  }, [orderedReportTracks, timelineData.reportMarkers, trackContentWidth, xPct]);

  const sampleCount = useMemo(
    () => timelineData.sampleMarkers.reduce(
      (total, marker) => total + (marker.groupCount ?? 1),
      0,
    ),
    [timelineData.sampleMarkers],
  );

  const reportCount = useMemo(
    () => timelineData.reportMarkers.reduce(
      (total, marker) => total + (marker.groupCount ?? 1),
      0,
    ),
    [timelineData.reportMarkers],
  );

  const handleTrackDragStart = (
    event: ReactDragEvent<HTMLElement>,
    section: ReorderableSectionKey,
    id: string,
  ): void => {
    event.stopPropagation();
    const { dataTransfer } = event;
    dataTransfer.effectAllowed = 'move';
    dataTransfer.setData('text/plain', id);
    setDraggedTrack({ id, section });
    setDropTargetTrack(null);
  };

  const handleTrackDragOver = (
    event: ReactDragEvent<HTMLElement>,
    section: ReorderableSectionKey,
    id: string,
  ): void => {
    if (!draggedTrack || draggedTrack.section !== section || draggedTrack.id === id) {
      return;
    }

    event.preventDefault();
    const { dataTransfer } = event;
    dataTransfer.dropEffect = 'move';
    setDropTargetTrack((prev) => {
      if (prev?.section === section && prev.id === id) {
        return prev;
      }

      return { id, section };
    });
  };

  const handleTrackDrop = (
    event: ReactDragEvent<HTMLElement>,
    section: ReorderableSectionKey,
    targetId: string,
  ): void => {
    event.preventDefault();
    event.stopPropagation();

    if (!draggedTrack || draggedTrack.section !== section || draggedTrack.id === targetId) {
      setDraggedTrack(null);
      setDropTargetTrack(null);
      return;
    }

    setTrackOrder((prev) => ({
      ...prev,
      [section]: reorderTrackIds(prev[section], draggedTrack.id, targetId),
    }));
    setDraggedTrack(null);
    setDropTargetTrack(null);
  };

  const handleTrackDragEnd = (): void => {
    setDraggedTrack(null);
    setDropTargetTrack(null);
  };

  const handleToggleSection = (section: SectionKey): void => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleToggleEvents = (): void => handleToggleSection('events');

  const handleToggleSamples = (): void => handleToggleSection('samples');

  const handleToggleReports = (): void => handleToggleSection('reports');

  const handleToggleTreatments = (): void => handleToggleSection('treatments');

  const handleToggleDiseaseBurden = (): void => handleToggleSection('diseaseBurden');

  const handleToggleBiomarker = (): void => handleToggleSection('biomarker');

  const handleRetrySamples = (): void => onRetrySamples?.();
  const handleRetryBiomarkers = (): void => onRetryBiomarkers?.();
  const handleRetryDiseaseBurden = (): void => onRetryDiseaseBurden?.();

  const handleFitAll = (): void => {
    setView([0, 1]);
  };

  const [visibleStart, visibleEnd] = view;
  const visibleWidth = visibleEnd - visibleStart;
  const canZoomIn = visibleWidth - minViewWidth > VIEW_WIDTH_EPSILON;
  const canZoomOut = 1 - visibleWidth > VIEW_WIDTH_EPSILON;

  const handleZoom = (factor: number): void => {
    const midpoint = (visibleStart + visibleEnd) / 2;
    const halfWidth = Math.min(1, Math.max(minViewWidth, visibleWidth * factor)) / 2;
    setView(clampView([midpoint - halfWidth, midpoint + halfWidth], minViewWidth));
  };

  const handleZoomIn = (): void => handleZoom(0.65);

  const handleZoomOut = (): void => handleZoom(1.5);

  const handleMonitoring = (): void => {
    const timepoints = timelineData.sampleMarkers
      .map((marker) => getValidDate(marker.date)?.valueOf())
      .filter((value): value is number => value !== undefined);

    if (!timepoints.length) {
      setView([0, 1]);
      return;
    }

    const { startMs, endMs } = timelineData.range;
    const total = endMs - startMs;
    const padding = MONITORING_VIEW_PADDING_DAYS * DAY_MS;
    const start = (Math.min(...timepoints) - padding - startMs) / total;
    const end = (Math.max(...timepoints) + padding - startMs) / total;
    setView(clampView([start, end], minViewWidth));
  };

  const cancelHoverClose = (): void => {
    if (closeHoverTimer.current) {
      clearTimeout(closeHoverTimer.current);
      closeHoverTimer.current = undefined;
    }
  };

  const scheduleHoverClose = (): void => {
    if (closeHoverTimer.current) {
      return;
    }

    closeHoverTimer.current = setTimeout(() => {
      closeHoverTimer.current = undefined;
      lastHoverKey.current = '';
      setHover(undefined);
    }, HOVER_CLOSE_DELAY_MS);
  };

  const clearTrackHover = (): void => {
    cancelHoverClose();
    lastHoverKey.current = '';
    setAxisHover(null);
    setHover(undefined);
  };

  const handleTrackHover = (event: ReactMouseEvent<HTMLElement>): void => {
    const wrapper = event.currentTarget;
    const rect = wrapper.getBoundingClientRect();
    const contentWidth = rect.width - LABEL_WIDTH;
    if (contentWidth <= 0) {
      return;
    }

    const offsetX = event.clientX - rect.left - LABEL_WIDTH;
    if (offsetX < 0 || offsetX > contentWidth) {
      clearTrackHover();
      return;
    }

    const cursorPct = (offsetX / contentWidth) * 100;
    const [viewStart, viewEnd] = view;
    const cursorFraction = viewStart + (cursorPct / 100) * (viewEnd - viewStart);
    const cursorMs = timelineData.range.startMs
      + cursorFraction * (timelineData.range.endMs - timelineData.range.startMs);
    const padX = 12;
    const padY = 4;
    const dots = Array.from(wrapper.querySelectorAll('[data-marker-date]'));
    let hitEl: Element | null = null;
    let hitRect: DOMRect | null = null;
    let bestDistanceX = Infinity;
    for (let i = 0; i < dots.length; i += 1) {
      const dot = dots[i];
      const dotRect = dot.getBoundingClientRect();
      const withinY = event.clientY >= dotRect.top - padY
        && event.clientY <= dotRect.bottom + padY;
      const withinX = event.clientX >= dotRect.left - padX
        && event.clientX <= dotRect.right + padX;
      const distanceX = Math.abs(dotRect.left + dotRect.width / 2 - event.clientX);
      if (withinY && withinX && distanceX < bestDistanceX) {
        bestDistanceX = distanceX;
        hitEl = dot;
        hitRect = dotRect;
      }
    }

    const hitDate = hitEl ? hitEl.getAttribute('data-marker-date') : null;
    const hitId = hitEl ? hitEl.getAttribute('data-marker-id') : null;

    const key = hitDate ? `hit:${hitDate}:${hitId}` : `free:${Math.round(cursorPct * 20)}`;
    if (key === lastHoverKey.current) {
      return;
    }
    lastHoverKey.current = key;

    setAxisHover({
      pct: hitDate ? xPct(hitDate) : cursorPct,
      date: dayjs(hitDate ?? cursorMs).format('D MMM YYYY'),
      hitDate,
      hitId,
    });

    const timelineContent = hitDate ? timelinePopupContent.get(hitDate) : undefined;
    const reportContent = hitDate && hitId && reportMarkerIds.has(hitId)
      ? reportPopupContent.get(hitDate)
      : undefined;
    const popupContent = reportContent ?? timelineContent;
    // Keyed on the date so a popup opened on another dot starts with its groups collapsed.
    const popupNode: ReactNode = popupContent
      ? <TimelinePopup key={popupContent.date} content={popupContent} patientId={patientId} />
      : null;

    if (popupNode && hitRect) {
      const dotRect: DOMRect = hitRect;
      cancelHoverClose();
      setHover({
        left: dotRect.left,
        right: dotRect.right,
        top: dotRect.top,
        bottom: dotRect.bottom,
        isInteractive: true,
        node: popupNode,
      });
    } else {
      scheduleHoverClose();
    }
  };

  const handleTrackHoverLeave = (): void => {
    lastHoverKey.current = '';
    setAxisHover(null);
    if (hover?.isInteractive) {
      scheduleHoverClose();
      return;
    }
    setHover(undefined);
  };

  const handleHoverCardEnter = (): void => cancelHoverClose();

  const handleHoverCardLeave = (): void => clearTrackHover();

  useEffect(() => () => {
    if (closeHoverTimer.current) {
      clearTimeout(closeHoverTimer.current);
    }
  }, []);

  return (
    <TimelineCard>
      <StickyTimelineHeader>
        <Header
          patientId={patientId}
          canZoomIn={canZoomIn}
          canZoomOut={canZoomOut}
          onFitAll={handleFitAll}
          onMonitoring={handleMonitoring}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
        />

        <HelperText>
          Drag the overview box to pan - Drag its edges to zoom - Double-click overview
          to reset
        </HelperText>

        <Overview
          view={view}
          eventMarkers={timelineData.eventMarkers}
          sampleMarkers={timelineData.sampleMarkers}
          range={timelineData.range}
          onViewChange={setView}
        />

        <MonthAxis
          range={timelineData.range}
          view={view}
          xPct={xPct}
          hover={axisHover}
        />
      </StickyTimelineHeader>

      <TimelinePanel>
        {axisHover && <AxisCrosshair pct={axisHover.pct} />}

        <InnerWrapper
          ref={panelRef}
          onMouseMove={handleTrackHover}
          onMouseLeave={handleTrackHoverLeave}
        >
          <Section
            title="Events"
            count={timelineData.eventMarkers.length}
            isLoading={isEventsLoading}
            isOpen={openSections.events}
            onToggle={handleToggleEvents}
          >
            <TrackRow label="Events">
              {timelineData.eventMarkers
                .filter((marker) => isInView(marker.date))
                .map((marker) => (
                  <Marker
                    key={marker.id}
                    marker={marker}
                    left={xPct(marker.date)}
                    isActive={axisHover?.hitId === marker.id}
                  />
                ))}
            </TrackRow>
          </Section>

          <Section
            title="Samples"
            count={sampleCount}
            isLoading={isSamplesLoading}
            isOpen={openSections.samples}
            onToggle={handleToggleSamples}
          >
            {hasSamplesFailure ? (
              <LaneError
                lane="track"
                message="Could not load patient biomaterials."
                onRetry={handleRetrySamples}
              />
            ) : orderedSampleTracks.map((track) => (
              <TrackRow
                key={track.id}
                label={track.label}
                section="samples"
                id={track.id}
                isDragging={draggedTrack?.section === 'samples' && draggedTrack.id === track.id}
                isDropTarget={dropTargetTrack?.section === 'samples' && dropTargetTrack.id === track.id}
                onDragStart={handleTrackDragStart}
                onDragOver={handleTrackDragOver}
                onDrop={handleTrackDrop}
                onDragEnd={handleTrackDragEnd}
              >
                {track.markers
                  .filter((marker) => isInView(marker.date))
                  .map((marker) => (
                    <Marker
                      key={marker.id}
                      marker={marker}
                      left={xPct(marker.date)}
                      isActive={axisHover?.hitId === marker.id}
                      labelWidth={sampleLabelWidths.get(marker.id)}
                    />
                  ))}
              </TrackRow>
            ))}
          </Section>

          <Section
            title="Reports"
            count={reportCount}
            isLoading={isReportsLoading}
            isOpen={openSections.reports}
            onToggle={handleToggleReports}
          >
            {orderedReportTracks.map((track) => (
              <TrackRow
                key={track.id}
                label={track.label}
                section="reports"
                id={track.id}
                isDragging={draggedTrack?.section === 'reports' && draggedTrack.id === track.id}
                isDropTarget={dropTargetTrack?.section === 'reports' && dropTargetTrack.id === track.id}
                onDragStart={handleTrackDragStart}
                onDragOver={handleTrackDragOver}
                onDrop={handleTrackDrop}
                onDragEnd={handleTrackDragEnd}
              >
                {timelineData.reportMarkers
                  .filter((marker) => marker.reportTrackId === track.id && isInView(marker.date))
                  .map((marker) => (
                    <Marker
                      key={marker.id}
                      marker={marker}
                      left={xPct(marker.date)}
                      isActive={axisHover?.hitId === marker.id}
                      labelWidth={reportLabelWidths.get(marker.id)}
                    />
                  ))}
              </TrackRow>
            ))}
          </Section>

          {IS_TREATMENTS_SECTION_VISIBLE && (
            <Section
              title="Treatments"
              count={0}
              isOpen={openSections.treatments}
              onToggle={handleToggleTreatments}
            />
          )}

          <DiseaseBurdenPanel
            data={diseaseBurdenData}
            scale={chartLaneScale}
            filters={filters}
            hiddenAnalytes={hiddenAnalytes}
            isLoading={diseaseBurden.isLoading}
            hasFailure={diseaseBurden.hasFailure}
            isOpen={openSections.diseaseBurden}
            onToggle={handleToggleDiseaseBurden}
            onToggleAssay={toggleAssay}
            onSetMode={setAssayMode}
            onToggleVisibility={toggleVisibility}
            onRetry={handleRetryDiseaseBurden}
            xPct={xPct}
          />

          <BiomarkerPanel
            data={biomarkerData}
            scale={chartLaneScale}
            selection={selection}
            isLoading={biomarker.isLoading}
            hasFailure={biomarker.hasFailure}
            isOpen={openSections.biomarker}
            onToggle={handleToggleBiomarker}
            onToggleVariant={toggleVariant}
            onRetry={handleRetryBiomarkers}
            xPct={xPct}
          />
        </InnerWrapper>
      </TimelinePanel>

      {hover && (
        <FloatingTooltip
          hover={hover}
          onMouseEnter={handleHoverCardEnter}
          onMouseLeave={handleHoverCardLeave}
        />
      )}
    </TimelineCard>
  );
}
